import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import notifications from "../../../common/Components/Notifications/Notification";
import { HKPImage } from "../../../media/HKPImage";
import { pickProductListPreviewRows, pickProductListThumbUrl } from "../previewMedia";
import { isDraftStatus } from "../productStockDisplay";
import { productBadge, productCompleteness } from "./productListRow";
import { PlIcon } from "./plIcons";

export type ProductListLayout = "phone" | "tablet" | "laptop";

const layoutNow = (): ProductListLayout => {
  if (typeof window === "undefined" || !window.matchMedia) return "laptop";
  if (window.matchMedia("(min-width: 1200px)").matches) return "laptop";
  if (window.matchMedia("(min-width: 768px)").matches) return "tablet";
  return "phone";
};

/** Cards on phone (<768), grid + filter pane on tablet (768–1199), table + pane on laptop (≥1200). */
export const useProductListLayout = (): ProductListLayout => {
  const [layout, setLayout] = useState<ProductListLayout>(layoutNow);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const queries = [window.matchMedia("(min-width: 768px)"), window.matchMedia("(min-width: 1200px)")];
    const onChange = () => setLayout(layoutNow());
    queries.forEach((q) => (q.addEventListener ? q.addEventListener("change", onChange) : q.addListener(onChange)));
    return () =>
      queries.forEach((q) => (q.removeEventListener ? q.removeEventListener("change", onChange) : q.removeListener(onChange)));
  }, []);
  return layout;
};

const reducedMotion = () =>
  typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

/**
 * Bottom sheet (phone/tablet). Slides up in 220ms, leaves in 180ms; reduced motion skips both.
 * Escape and the scrim close it. Body scroll is locked while it is open.
 */
export const PlSheet = ({
  open,
  onClose,
  label,
  className = "",
  children,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  className?: string;
  children: (close: () => void) => React.ReactNode;
}) => {
  const [closing, setClosing] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const close = useCallback(() => {
    if (reducedMotion()) {
      onClose();
      return;
    }
    setClosing(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setClosing(false);
      onClose();
    }, 180);
  }, [onClose]);
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => () => clearTimeout(timer.current), []);
  // Runs once per opening (not on every parent render) so focus stays in a field being typed in.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const previous = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className={`pl-sheet-root${closing ? " is-closing" : ""}`}>
      <div className="pl-scrim" onClick={close} aria-hidden="true" />
      <div
        className={`pl-sheet ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="pl-sheet__handle" aria-hidden="true" />
        {children(close)}
      </div>
    </div>,
    document.body
  );
};

/** Stock switch: changes instantly (no animation), the page handles the optimistic save. */
export const StockSwitch = ({ product, onToggle }: { product: any; onToggle: (product: any, inStock: boolean) => void }) => {
  // A draft is still being made: it shows as out of stock and publishing puts it in stock.
  const draft = isDraftStatus(product?.status);
  const on = !draft && product?.inStock === true;
  return (
    <span className="pl-stock" title={draft ? "Publishing marks it in stock" : undefined}>
      <span className="pl-stock__label" aria-hidden="true">
        Stock
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        // Not `disabled`: a tap on a draft explains that publishing puts it in stock.
        aria-disabled={draft || undefined}
        aria-label={
          draft ? "Out of stock. Draft: publishing marks it in stock" : on ? "In stock. Mark out of stock" : "Out of stock. Mark in stock"
        }
        className={`pl-switch${on ? " is-on" : ""}`}
        onClick={(event) => {
          event.stopPropagation();
          onToggle(product, !on);
        }}
      >
        <span className="pl-switch__knob" />
      </button>
    </span>
  );
};

export const CompletenessDots = ({ product }: { product: any }) => {
  const dots = productCompleteness(product);
  const done = dots.filter((dot) => dot.state === "full").length;
  const word = { full: "done", partial: "started", none: "not started" } as const;
  // Touch screens have no hover title: a tap says what the five dots mean.
  return (
    <button
      type="button"
      className="pl-dots"
      aria-label={`${done} of 5 stages complete. Show stages`}
      onClick={(event) => {
        event.stopPropagation();
        notifications({
          title: `${done} of 5 stages done`,
          message: dots.map((dot) => `${dot.label}: ${word[dot.state]}`).join(" · "),
          type: "info",
        });
      }}
    >
      {dots.map((dot) => (
        <i key={dot.key} className={`pl-dot is-${dot.state}`} title={`${dot.label}: ${dot.text}`} />
      ))}
    </button>
  );
};

export const ProductBadge = ({ product, draftRevisionsEnabled }: { product: any; draftRevisionsEnabled: boolean }) => {
  const badge = productBadge(product, draftRevisionsEnabled);
  return (
    <span className={`pl-badge is-${badge.kind}`} title={badge.kind === "edits" ? "Live, with edits saved as draft" : undefined}>
      {badge.label}
    </span>
  );
};

export const ProductThumb = ({
  product,
  className = "",
  onPreview,
  preset = "THUMBNAIL",
}: {
  product: any;
  className?: string;
  onPreview: (images: any) => void;
  preset?: "THUMBNAIL" | "PRODUCT_CARD";
}) => {
  const url = pickProductListThumbUrl(product?.thumbnailDetails?.media, preset);
  if (!url) {
    return (
      <span className={`pl-thumb is-empty ${className}`} aria-label="No photo yet">
        No photo
      </span>
    );
  }
  return (
    <button
      type="button"
      className={`pl-thumb ${className}`}
      aria-label={`Preview photos of ${product?.name || "product"}`}
      onClick={(event) => {
        event.stopPropagation();
        onPreview(pickProductListPreviewRows(product?.thumbnailDetails?.media));
      }}
    >
      <HKPImage preset={preset} legacyUrl={url} alt="" />
    </button>
  );
};

export const IconButton = ({
  icon,
  label,
  onClick,
  className = "",
}: {
  icon: string;
  label: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}) => (
  <button type="button" className={`pl-iconbtn ${className}`} aria-label={label} title={label} onClick={onClick}>
    <PlIcon name={icon} size={18} />
  </button>
);
